/**
 * Canonical placeholder key identifiers. Authors type one of these strings into
 * a placeholder's `placeholderKey` field in the editor inspector; the render
 * page's resolver swaps the placeholder for the matching value at export time.
 *
 * Values are stable identifiers — renaming a value breaks every board that
 * already stored that key. Add new members; do not rename existing ones.
 */
export const PlaceholderKey = {
  TEAM_NAME: 'team_name',
  TEAM_SHORT_NAME: 'team_short_name',
  TEAM_LOGO: 'team_logo',
  SCORE: 'score',
  SCORE_TEAM: 'score_team',
  CHAMPIONSHIP_NAME: 'championship_name',
  TOURNAMENT_NAME: 'tournament_name',
  TOURNAMENT_LOGO: 'tournament_logo',
  MATCH_DATE: 'match_date',
  MATCH_TIME: 'match_time',
  MATCH_VENUE: 'match_venue',
  ROUND_NAME: 'round_name',
  PLAYER_NAME: 'player_name',
  PLAYER_NUMBER: 'player_number',
  PLAYER_PHOTO: 'player_photo',
  GROUP_NAME: 'group_name',
  TOURNAMENT_POSITION: 'tournament_position',
  GAMES_PLAYED: 'games_played',
  GAMES_WON: 'games_won',
  GAMES_LOST: 'games_lost',
  GAMES_DRAWN: 'games_drawn',
  GOALS_SCORED: 'goals_scored',
  GOALS_CONCEDED: 'goals_conceded',
  GOAL_DIFFERENCE: 'goal_difference',
  GAMES_DIFFERENCE: 'games_difference',
  POINTS: 'points',
  // Per-championship player statistics — exclusive to the player-statistics
  // template. Deliberately NOT reusing GAMES_PLAYED / GOALS_SCORED / POINTS
  // above: those resolve a *team's* standings row, these resolve a *player's*
  // stats line. Reusing them would make a key's meaning depend on the enclosing
  // template type, and the reuse would only ever be partial anyway (there is no
  // player analogue for GOALS_CONCEDED, GOAL_DIFFERENCE, POINTS or
  // TOURNAMENT_POSITION).
  PLAYER_POSITION: 'player_position',
  PLAYER_GAMES_PLAYED: 'player_games_played',
  PLAYER_GOALS: 'player_goals',
  PLAYER_ASSISTS: 'player_assists',
  PLAYER_GOALS_ASSISTS: 'player_goals_assists',
  PLAYER_YELLOW_CARDS: 'player_yellow_cards',
  PLAYER_RED_CARDS: 'player_red_cards',
  // Multi-line list of one team's goal scorers with minutes — single-match
  // RESULTS only. Resolved by the render service from the match's goal events.
  GOAL_SCORERS: 'goal_scorers',
  // Playoff bracket. STAGE_NAME is stage-level metadata; the other two live on
  // one side of a bracket pair. TEAM_SLOT_PLACEHOLDER is the node's text for a
  // team not decided yet («Победитель A1») — a key of its own rather than a
  // fallback inside TEAM_NAME, so a designer can style the two differently.
  STAGE_NAME: 'stage_name',
  SCORE_PENALTY_TEAM: 'score_penalty_team',
  TEAM_SLOT_PLACEHOLDER: 'team_slot_placeholder',
  // Birthday card of one player.
  PLAYER_BIRTH_DATE: 'player_birth_date',
  PLAYER_AGE: 'player_age',
  // Symbolic team. The first two describe the team itself; the slot position is
  // the label of the place in the formation («Левый защитник»).
  SYMBOLIC_TEAM_NAME: 'symbolic_team_name',
  SYMBOLIC_TEAM_FORMATION: 'symbolic_team_formation',
  SYMBOLIC_SLOT_POSITION: 'symbolic_slot_position',
} as const;

export type PlaceholderKey = (typeof PlaceholderKey)[keyof typeof PlaceholderKey];

const KNOWN_KEYS: ReadonlySet<string> = new Set(Object.values(PlaceholderKey));

export function isKnownPlaceholderKey(value: string | undefined | null): value is PlaceholderKey {
  return typeof value === 'string' && KNOWN_KEYS.has(value);
}
